import { Snackbar } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useEffect, useRef, useState } from 'react';
import {
    getCaseTabSlugs,
    trackCreateCase,
    trackUploadCaseDocuments,
    trackViewTab,
} from './analytics';
import { useNavigate, useParams } from 'react-router-dom';
import AcceptTerms from './AcceptTerms';
import { useAuth } from './AuthContext';
import './App.css';
import './Case.css';
import CaseSummaryHeader from './CaseSummaryHeader';
import Discovery from './Discovery.js';
import DocumentsOverview from './DocumentsOverview';
import Drafting from './Drafting.js';
import IncidentDetails from './IncidentDetails.js';
import LawAppBar from './LawAppBar';
import LoginScreen from './LoginScreen';
import {
    handleGoogleLoginSuccess,
    renderGoogleLoginButton,
} from './LoginUtils';
import MedicalChronology from './MedicalChronology.js';
import { caseHasDiscoveryEvidence } from './PathUtils.js';
import MedicalExpenses from './MedicalExpenses.js';
import SubscriptionRequiredBanner from './SubscriptionRequiredBanner';
import SubscriptionRequiredDialog from './SubscriptionRequiredDialog';
import { hasProductAccess } from './SubscriptionRequiredContent';
import SourceDocumentViewer from './SourceDocumentViewer.js';
import TabFeedback from './TabFeedback.js';
import TimelineView from './TimelineView.js';
import {
    getDocumentUploadOutcomeMessage,
    isFileTooLarge,
    reportDocumentUploadFailure,
    revertUploadEntries,
} from './documentUploadFailure';
import { respondToUnauthorized } from './sessionUnauthorized';

function CustomTabPanel(props) {
    const { children, selectedTabValue, myValue, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={selectedTabValue !== myValue}
            id={`simple-tabpanel-${myValue}`}
            aria-labelledby={`simple-tab-${myValue}`}
            {...other}
        >
            {selectedTabValue === myValue && (
                <Box sx={{
                    p: 0
                }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function Case() {
    const { caseId } = useParams();
    const {
        isAuthenticated,
        isLoading: authLoading,
        user,
        setAuthenticatedUser,
        logout,
    } = useAuth();
    // server side state
    const [caseInfo, setCaseInfo] = useState(null);
    const [termsAcceptanceRequired, setTermsAcceptanceRequired] = useState(null);

    // client side state
    const [snackbarMessage, setSnackBarMessage] = useState(null);
    const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
    // Contains fields 'color', 'selected', and 'status'.
    const [fileNamesToFilePropertiesMap, setFileNamesToFilePropertiesMap] = useState({});
    const refFileNamesToFilePropertiesMap = useRef();
    // Without using reference, value within fetchCaseInfoAndUpdateUI becomes stale when state
    // is updated from .
    refFileNamesToFilePropertiesMap.current = fileNamesToFilePropertiesMap;

    // Map with filenames that are being uploaded and state has not reached backend DB.
    // Key is filename, value is timestamp until which we wait for 'processing'status to reach DB.
    // We keep uploaded files in this set until the timestamp is reached or until DB status is processing, whichever occurs first.
    const [uploadingFileNamesSet, setUploadingFileNamesSet] = useState({});
    // Tracks tabs where the user has already submitted or dismissed feedback this session.
    const dismissedTabsRef = useRef(new Set());

    // Index of timeline event that was selected by user(if any)
    const [selectedEventIndex, setSelectedEventIndex] = useState(-1);
    // Name of document that is being displayed in DocumentSourceViewer, if any.
    const [displayedDocument, setDisplayedDocument] = useState(null);
    const [selectedTabValue, setSelectedTabValue] = useState(0);
    const lastTrackedTabRef = useRef({ tabIndex: null, visibilityKey: null });
    const navigate = useNavigate();
    var colorIndex = 0;
    const colors = ['#FF000020', '#50D0D060', '#2F4F4F50', '#D0D05090', '#5050D040', '#50D05070', '#D0505050', '#50505050', '#D050D030'];

    const subscriptionOk = hasProductAccess(user);

    function onGoogleLoginSuccess(response) {
        handleGoogleLoginSuccess(response, onLoginComplete).catch(handleLoginError);
    }

    function onEmailLoginSuccess(user) {
        onLoginComplete(user);
    }

    function onLoginComplete(user) {
        if (user) {
            setAuthenticatedUser(user);
        }
        if (!hasProductAccess(user)) {
            navigate('/account');
            return;
        }
        fetchCaseInfoAndUpdateUI();
    }

    function handleLoginError(err) {
        console.error('Login failed:', err);
        doLogout();
    }

    function renderLoginButton() {
        renderGoogleLoginButton("googleLogin", onGoogleLoginSuccess);
    }

    function doLogout() {
        logout(renderLoginButton);
    }

    useEffect(() => {
        if (authLoading) {
            return;
        }
        if (isAuthenticated) {
            fetchCaseInfoAndUpdateUI();
        }
    }, [authLoading, isAuthenticated]);

    // Prefetch document sources when caseInfo is loaded or updated
    useEffect(() => {
        if (!caseInfo || !caseInfo['documents']) {
            return;
        }

        const prefetchLinks = [];

        // Create prefetch links for each document
        for (let docKey in caseInfo['documents']) {
            const docInfo = caseInfo['documents'][docKey];
            const filename = docInfo?.['filename'];

            // Only prefetch if document has been processed and is available
            if (filename && ('pdf' in docInfo || 'txt' in docInfo)) {
                const link = document.createElement('link');
                link.rel = 'prefetch';
                link.href = `/api/documents/${caseInfo['caseId']}/${filename}`;
                link.as = 'document';
                document.head.appendChild(link);
                prefetchLinks.push(link);
            }
        }

        // Cleanup: remove prefetch links when component unmounts or caseInfo changes
        return () => {
            prefetchLinks.forEach(link => {
                if (link.parentNode) {
                    document.head.removeChild(link);
                }
            });
        };
    }, [caseInfo]);

    useEffect(() => {
        if (!caseInfo || Object.keys(caseInfo.documents || {}).length === 0) {
            return;
        }

        const tabSlugs = getCaseTabSlugs(caseInfo);
        const visibilityKey = tabSlugs.join(',');
        const tabName = tabSlugs[selectedTabValue];
        if (!tabName) {
            return;
        }
        if (lastTrackedTabRef.current.tabIndex === selectedTabValue
            && lastTrackedTabRef.current.visibilityKey === visibilityKey) {
            return;
        }

        lastTrackedTabRef.current = { tabIndex: selectedTabValue, visibilityKey };
        trackViewTab(tabName);
    }, [caseInfo, selectedTabValue]);

    // console.log('Rendering lawapp with caseInfo: ' + JSON.stringify(caseInfo));
    if (authLoading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                background: 'var(--background-color)',
            }}>
                <img src="/loading.gif" alt="loading..." style={{ height: '48px' }} />
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <LoginScreen
                onGoogleLoginSuccess={onGoogleLoginSuccess}
                onEmailLoginSuccess={onEmailLoginSuccess}
            />
        );
    }

    if (termsAcceptanceRequired) {
        return (
            <AcceptTerms
                termsType={termsAcceptanceRequired}
                onTermsAccepted={handleTermsAccepted}
            />
        );
    }

    if (caseInfo === null) {
        return (
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: '100vh',
                background: 'var(--background-color)'
            }}>
                <img
                    src="/loading.gif"
                    alt="loading..."
                    style={{
                        height: "50px",
                        filter: 'opacity(0.8)'
                    }}
                />
            </div>
        );
    }
    let currentTabValue = 0;

    const shouldShowDiscoveryTab = caseHasDiscoveryEvidence(caseInfo);
    // Compute whether all AI processing queues are idle for the feedback prompt.
    const isGlobalProcessingComplete = (
        Object.keys(caseInfo.documents || {}).length > 0 &&
        !(caseInfo['analysis_queue']?.length > 0) &&
        Object.values(caseInfo['documents'] || {}).every(doc => !(doc['analysis_queue']?.length > 0))
    );
    const feedbackTabSlugs = getCaseTabSlugs(caseInfo);
    const currentFeedbackTabSlug = feedbackTabSlugs[selectedTabValue] || null;

    const shouldShowIncidentDetailsTab = (caseInfo['documents'] && Object.keys(caseInfo['documents']).some(filename => caseInfo['documents'][filename]['analysis']?.incident_details?.length > 0))
        || (caseInfo['analysis']?.incident_summary?.incident_date && !isNaN(new Date(caseInfo['analysis'].incident_summary.incident_date).getTime()))
        // || COLLISION_REPORT_CASE_TYPES.includes((caseInfo['case_type'] || '').toLowerCase())
        ;
    return (
        <div className="App">
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: "center" }}
                open={snackbarMessage !== null}
                autoHideDuration={3000}
                onClose={() => setSnackBarMessage(null)}
                message={snackbarMessage}
                sx={{
                    '& .MuiSnackbarContent-message': {
                        textAlign: 'center',
                        width: '100%'
                    }
                }}
            />
            <LawAppBar />
            <Box sx={{
                margin: "0 auto",
                padding: "0 20px"
            }}>
                {!subscriptionOk && <SubscriptionRequiredBanner />}
                <CaseSummaryHeader
                    caseInfo={caseInfo}
                    setCaseInfo={setCaseInfo}
                    refFileNamesToFilePropertiesMap={refFileNamesToFilePropertiesMap}
                    setFileNamesToFilePropertiesMap={setFileNamesToFilePropertiesMap}
                    uploadingFileNamesSet={uploadingFileNamesSet}
                    onFilesSelectedForUpload={onFilesSelectedForUpload}
                    setSnackBarMessage={setSnackBarMessage}
                    subscriptionOk={subscriptionOk}
                    onSubscriptionRequired={() => setSubscriptionDialogOpen(true)}
                />

                <SubscriptionRequiredDialog
                    open={subscriptionDialogOpen}
                    onClose={() => setSubscriptionDialogOpen(false)}
                />

                <Box sx={{
                    width: '100%',
                    visibility: Object.keys(caseInfo['documents']).length > 0 ? "visible" : "hidden",
                    mt: 4
                }}>
                    <Box sx={{
                        borderBottom: '1px solid var(--border-color)',
                        backgroundColor: 'var(--background-color)',
                        borderRadius: '12px 12px 0 0',
                        overflow: 'hidden'
                    }}>
                        <Tabs
                            variant="scrollable"
                            scrollButtons="auto"
                            value={selectedTabValue}
                            onChange={handleTabChange}
                            aria-label="Information from selected documents"
                            sx={{
                                '& .MuiTabs-indicator': {
                                    backgroundColor: 'var(--accent-color)',
                                    height: '3px'
                                },
                                '& .MuiTab-root': {
                                    color: 'var(--text-secondary)',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    fontSize: '0.95rem',
                                    minHeight: '60px',
                                    padding: '12px 24px',
                                    '&.Mui-selected': {
                                        color: 'var(--text-primary)',
                                        fontWeight: 600
                                    },
                                    '&:hover': {
                                        backgroundColor: 'var(--surface-hover)',
                                        color: 'var(--text-primary)'
                                    }
                                }
                            }}
                        >
                            <Tab label="Documents Overview" />
                            {shouldShowIncidentDetailsTab && <Tab label="Incident Details" />}
                            <Tab label="Medical Chronology" />
                            <Tab label="Medical Expenses" />
                            <Tab label="Timeline of Events" />
                            {shouldShowDiscoveryTab && <Tab label="Discovery" />}
                            <Tab label="Drafting" />
                        </Tabs>
                    </Box>
                    <Box sx={{
                        backgroundColor: 'var(--background-color)',
                        border: '1px solid var(--border-color)',
                        borderTop: 'none',
                        borderRadius: '0 0 12px 12px',
                        minHeight: '400px'
                    }}>
                        <CustomTabPanel selectedTabValue={selectedTabValue} myValue={currentTabValue++}>
                            <DocumentsOverview caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                fileNamesToFilePropertiesMap={fileNamesToFilePropertiesMap}
                                displayedDocumentName={displayedDocument}
                                setDisplayedDocument={setDisplayedDocument}
                                setSnackBarMessage={setSnackBarMessage} />
                        </CustomTabPanel>
                        {shouldShowIncidentDetailsTab && <CustomTabPanel selectedTabValue={selectedTabValue} myValue={currentTabValue++}>
                            <IncidentDetails caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                fileNamesToFilePropertiesMap={fileNamesToFilePropertiesMap}
                                displayedDocumentName={displayedDocument}
                                setDisplayedDocument={setDisplayedDocument}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </CustomTabPanel>}
                        <CustomTabPanel selectedTabValue={selectedTabValue} myValue={currentTabValue++}>
                            <MedicalChronology caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                fetchCaseInfoAndUpdateUI={fetchCaseInfoAndUpdateUI}
                                fileNamesToFilePropertiesMap={fileNamesToFilePropertiesMap}
                                displayedDocumentName={displayedDocument}
                                setDisplayedDocument={setDisplayedDocument}
                                setSnackBarMessage={setSnackBarMessage}
                                subscriptionOk={subscriptionOk}
                                onSubscriptionRequired={() => setSubscriptionDialogOpen(true)}
                            />
                        </CustomTabPanel>
                        <CustomTabPanel selectedTabValue={selectedTabValue} myValue={currentTabValue++}>
                            <MedicalExpenses caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                fetchCaseInfoAndUpdateUI={fetchCaseInfoAndUpdateUI}
                                fileNamesToFilePropertiesMap={fileNamesToFilePropertiesMap}
                                displayedDocument={displayedDocument}
                                setDisplayedDocument={setDisplayedDocument}
                                setSnackBarMessage={setSnackBarMessage}
                                subscriptionOk={subscriptionOk}
                                onSubscriptionRequired={() => setSubscriptionDialogOpen(true)}
                            />
                        </CustomTabPanel>
                        <CustomTabPanel selectedTabValue={selectedTabValue} myValue={currentTabValue++}>
                            <TimelineView caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                fileNamesToFilePropertiesMap={fileNamesToFilePropertiesMap}
                                displayedDocumentName={displayedDocument}
                                setDisplayedDocument={setDisplayedDocument}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </CustomTabPanel>
                        {shouldShowDiscoveryTab && <CustomTabPanel selectedTabValue={selectedTabValue} myValue={currentTabValue++}>
                            <Discovery caseInfo={caseInfo}
                                fileNamesToFilePropertiesMap={fileNamesToFilePropertiesMap}
                                displayedDocumentName={displayedDocument}
                                setDisplayedDocument={setDisplayedDocument}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </CustomTabPanel>}
                        <CustomTabPanel selectedTabValue={selectedTabValue} myValue={currentTabValue++}>
                            <Drafting caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                setSnackBarMessage={setSnackBarMessage}
                                setDisplayedDocument={setDisplayedDocument}
                                fetchCaseInfoAndUpdateUI={fetchCaseInfoAndUpdateUI}
                                subscriptionOk={subscriptionOk} />
                        </CustomTabPanel>
                    </Box>
                </Box>
            </Box>
            <SourceDocumentViewer caseInfo={caseInfo}
                displayedDocument={displayedDocument}
                sourceDocumentViewerOnClose={sourceDocumentViewerOnClose} />
            {currentFeedbackTabSlug && currentFeedbackTabSlug !== 'drafting' && (
                <TabFeedback
                    caseId={caseId}
                    tabSlug={currentFeedbackTabSlug}
                    processingComplete={isGlobalProcessingComplete}
                    dismissedTabsRef={dismissedTabsRef}
                />
            )}
        </div >
    );

    function handleTabChange(event, newValue) {
        setSelectedTabValue(newValue);
    }

    function sourceDocumentViewerOnClose() {
        setSelectedEventIndex(-1);
        setDisplayedDocument(null);
    }

    function fetchCaseInfoAndUpdateUI() {
        console.log('fetchCaseInfoAndUpdateUI calling: /api/case/' + caseId);

        fetch('/api/case/' + caseId, {
            method: 'GET',
            credentials: 'same-origin'
        })
            .then(res => {
                if (res.status === 401) {
                    doLogout();
                    return "{}";
                }
                if (res.status === 403) {
                    const termsHeader = res.headers.get('X-Terms-Acceptance-Required');
                    if (termsHeader === 'new-terms') {
                        setTermsAcceptanceRequired(termsHeader);
                        return "{}";
                    }
                    navigate('/mycases');
                    return "{}";
                }
                return res.json();
            })
            .then(json => {
                let newCaseInfo = json?.case;

                if (newCaseInfo) {
                    updateUIWithNewCaseInfo(newCaseInfo);
                } else if (json && Object.keys(json).length > 0) {
                    console.log('FATAL: No caseInfo in response:' + json);
                }
            })
            .catch(err => {
                var data = {
                    err: err,
                    stack: err.stack,
                    context: 'fetchCaseInfoAndUpdateUI'
                }
                fetch('/api/jserrors', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: new Headers({ 'content-type': 'application/json' }),
                    body: JSON.stringify(data)
                });
                console.log('[FATAL]Unable to load case: ' + JSON.stringify(data));
            });
    }

    function handleTermsAccepted() {
        setTermsAcceptanceRequired(null);
        fetchCaseInfoAndUpdateUI();
    }

    function updateUIWithNewCaseInfo(newCaseInfo) {
        console.log('In updateUIWithNewCaseInfo');
        if (caseId === 'new' &&
            /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/.test(newCaseInfo['caseId'])) {
            trackCreateCase();
            navigate('/case/' + newCaseInfo['caseId']);
            console.log('Navigating to URL with caseId from /new');
        }
        const newFileNamesToFilePropertiesMap = {};
        colorIndex = 0;
        let hasDocsInProcessingStatus = false;
        for (let filekey in newCaseInfo['documents']) {
            const filename = newCaseInfo['documents'][filekey]['filename'];
            newFileNamesToFilePropertiesMap[filename] = {
                color: colors[colorIndex++],
                selected: (filename in refFileNamesToFilePropertiesMap.current) ? refFileNamesToFilePropertiesMap.current[filename]['selected'] : true,
                status: newCaseInfo['documents'][filekey]['status']
            }
            hasDocsInProcessingStatus = hasDocsInProcessingStatus || newCaseInfo['documents'][filekey]['status']?.includes('processing');
        }

        const newUploadingFileNamesSet = {};
        for (let filename in uploadingFileNamesSet) {
            hasDocsInProcessingStatus = true;
            // Only keep documents in newUploadingFileNamesSet if
            //    it is in DB, but status has not transitioned to 'processing' within allocated time
            if (!(filename in newFileNamesToFilePropertiesMap) || // file is not in DB 
                (!newFileNamesToFilePropertiesMap[filename]['status'].includes('processing') && // status is not 'processing'
                    uploadingFileNamesSet[filename] > new Date().getTime())) { // within allocated time to reach 'processing'
                newUploadingFileNamesSet[filename] = uploadingFileNamesSet[filename];

                newFileNamesToFilePropertiesMap[filename] = {
                    color: colors[colorIndex++],
                    selected: (filename in refFileNamesToFilePropertiesMap.current) ? refFileNamesToFilePropertiesMap.current[filename]['selected'] : true,
                    status: 'processing_ocr'
                }
            }
        }
        setCaseInfo(newCaseInfo);
        setFileNamesToFilePropertiesMap(newFileNamesToFilePropertiesMap);
        setUploadingFileNamesSet(newUploadingFileNamesSet);
        // console.log('New Case files: ' + Object.keys(newCaseInfo['documents']).join(', '));
        // console.log('Uploading files: ' + Object.keys(newUploadingFileNamesSet).join(', '));

        if (newCaseInfo['analysis_queue']?.length > 0 || hasDocsInProcessingStatus) {
            console.log('analysis_queue length: ' + newCaseInfo['analysis_queue']?.length + '; hasDocsInProcessingStatus:' + hasDocsInProcessingStatus);
            setTimeout(fetchCaseInfoAndUpdateUI, 10000);
        }
    }

    async function onFilesSelectedForUpload(file) {
        const files = Array.from(file);
        console.log('onFilesSelectedForUpload: ' + files[0]?.name);

        trackUploadCaseDocuments(files.length);

        const previousFileMap = fileNamesToFilePropertiesMap;
        const previousUploading = uploadingFileNamesSet;
        const tooLargeNames = files.filter(isFileTooLarge).map(f => f.name);
        const filesToUpload = files.filter(f => !isFileTooLarge(f));

        let newFileNamesToFilePropertiesMap = { ...fileNamesToFilePropertiesMap };
        let newUploadingFileNamesSet = { ...uploadingFileNamesSet };
        if (filesToUpload.length > 0) {
            for (const selectedFile of filesToUpload) {
                if (!(selectedFile.name in newFileNamesToFilePropertiesMap)) {
                    newFileNamesToFilePropertiesMap[selectedFile.name] = {
                        color: colors[colorIndex++],
                        selected: true,
                        status: 'processing_ocr'
                    }
                    newUploadingFileNamesSet[selectedFile.name] = new Date().getTime();
                } else if (!newFileNamesToFilePropertiesMap[selectedFile.name]['status'].includes('processing')) {
                    newFileNamesToFilePropertiesMap[selectedFile.name]['status'] = 'processing_ocr';
                    newUploadingFileNamesSet[selectedFile.name] = new Date().getTime() + 60 * 1000;
                }
            }
            setFileNamesToFilePropertiesMap(newFileNamesToFilePropertiesMap);
            setUploadingFileNamesSet(newUploadingFileNamesSet);
        }

        const failedNames = [];
        const namesToRevert = [];
        let anySucceeded = false;
        let stoppedForUnauthorized = false;

        for (let i = 0; i < filesToUpload.length; i++) {
            const selectedFile = filesToUpload[i];
            const reportContext = {
                caseId: caseInfo?.caseId,
                fileCount: 1,
                fileNames: [selectedFile.name],
            };
            const data = new FormData();
            data.append('file', selectedFile);

            let response;
            try {
                response = await fetch('/api/documents?caseId=' + caseInfo['caseId'], {
                    method: 'POST',
                    credentials: 'same-origin',
                    body: data,
                });
            } catch (e) {
                console.log('File upload failed: ' + e);
                await reportDocumentUploadFailure({
                    error: e,
                    ...reportContext,
                });
                failedNames.push(selectedFile.name);
                namesToRevert.push(selectedFile.name);
                continue;
            }

            console.log('Fileupload status: ' + response?.ok + '; Response:' + JSON.stringify(response));
            if (response?.ok) {
                anySucceeded = true;
                continue;
            }

            if (respondToUnauthorized(response, doLogout)) {
                stoppedForUnauthorized = true;
                namesToRevert.push(
                    selectedFile.name,
                    ...filesToUpload.slice(i + 1).map(f => f.name)
                );
                break;
            }

            let responseBody;
            try {
                responseBody = (await response.text()).slice(0, 200);
            } catch (_) { /* ignore */ }

            await reportDocumentUploadFailure({
                error: new Error(`Document upload failed with status: ${response?.status}`),
                ...reportContext,
                status: response?.status,
                responseBody,
            });
            namesToRevert.push(selectedFile.name);
            if (response?.status === 413) {
                tooLargeNames.push(selectedFile.name);
            } else {
                failedNames.push(selectedFile.name);
            }
        }

        if (namesToRevert.length > 0) {
            const reverted = revertUploadEntries(
                newFileNamesToFilePropertiesMap,
                newUploadingFileNamesSet,
                namesToRevert,
                previousFileMap,
                previousUploading
            );
            setFileNamesToFilePropertiesMap(reverted.nextMap);
            setUploadingFileNamesSet(reverted.nextUploading);
        }

        if (stoppedForUnauthorized) {
            return;
        }

        if (anySucceeded) {
            setTimeout(fetchCaseInfoAndUpdateUI, 10000);
        }

        const outcomeMessage = getDocumentUploadOutcomeMessage({ tooLargeNames, failedNames });
        if (outcomeMessage) {
            setSnackBarMessage(outcomeMessage);
        }
    }
}

export default Case;

const COLLISION_REPORT_CASE_TYPES = [
    'car accident',
    'truck accident',
    'motorcycle accident',
    'bicycle accident',
    'bus accident',
    'auto accident',
    'recreational vehicle accident',
    'traffic accident',
]

const PERSONAL_INJURY_CASE_TYPES = [
    'car accident',
    'truck accident',
    'motorcycle accident',
    'bicycle accident',
    'pedestrian accident',
    'boating accident',
    'bus accident',
    'auto accident',
    'train accident',
    'aircraft accident',
    'aviation accident',
    'recreational vehicle accident',
    'injuries from dui',
    'traffic accident',
    'motorcycle insurance claims',
    'insurance claims',
    'slip and fall',
    'dog bite',
    'animal attack',
    'swimming pool accident',
    'elevator accident',
    'escalator accident',
    'inadequate security',
    'negligent security',
    'fire and burn injury',
    'toxic substances exposure',
    'worker\'s compensation',
    'construction accident',
    'construction injury',
    'industrial accident',
    'medical malpractice',
    'birth injuries',
    'nursing home abuse and neglect',
    'pharmaceutical injury',
    'pain and suffering',
    'catastrophic injury',
    'wrongful death',
    'defective product',
    'dangerous drugs',
    'defective medical device',
    'sports and recreational accident',
    'product liability',
    'school and playground injury',
    'child injury',
    'food poisoning',
    'assault',
    'battery',
    'class action lawsuit',
    'brain injury',
    'traumatic brain injury',
    'spinal cord injury',
    'severe burn injury',
    'railroad accident',
    'maritime and jones act',
    'personal injury',
]